import { Controller } from "stimulus"
import dragula from "dragula"

export default class extends Controller {
  static get targets() {
    return ["board", "section", "sectionTemplate", "optionTemplate", "descriptionRuleTemplate", "blockDropzone", "blockLibrary"]
  }

  connect() {
    this.sectionKeyCounter = 1
    this.boundWindowScroll = this.syncLibraryPanelPosition.bind(this)
    this.boundWindowResize = this.syncLibraryPanelPosition.bind(this)
    this.setupBlockDrag()
    this.refreshHiddenFields()
    window.addEventListener("scroll", this.boundWindowScroll, { passive: true })
    window.addEventListener("resize", this.boundWindowResize)
    this.syncLibraryPanelPosition()
  }

  disconnect() {
    if (this.blockDrake) this.blockDrake.destroy()
    window.removeEventListener("scroll", this.boundWindowScroll)
    window.removeEventListener("resize", this.boundWindowResize)
  }

  addSection(event) {
    event.preventDefault()
    const node = this.sectionTemplateTarget.content.firstElementChild.cloneNode(true)
    this.boardTarget.appendChild(node)
    this.setupBlockDrag()
    this.refreshHiddenFields()
  }

  addSectionBelow(event) {
    event.preventDefault()
    const currentSection = event.currentTarget.closest('[data-target~="proposal-template-mapper.section"]')
    if (!currentSection) return
    const node = this.sectionTemplateTarget.content.firstElementChild.cloneNode(true)
    currentSection.insertAdjacentElement("afterend", node)
    this.setupBlockDrag()
    this.refreshHiddenFields()
  }

  addOption(event) {
    event.preventDefault()
    const section = event.currentTarget.closest('[data-target~="proposal-template-mapper.section"]')
    if (!section) return

    const row = section.querySelector(".row")
    if (!row) return

    const node = this.optionTemplateTarget.content.firstElementChild.cloneNode(true)
    const nextName = this.nextOptionNameForSection(section)
    const optionNameInput = node.querySelector('[data-role="option-name"]')
    if (optionNameInput) optionNameInput.value = nextName
    row.appendChild(node)
    this.setupBlockDrag()
    this.refreshHiddenFields()
  }

  removeOption(event) {
    event.preventDefault()
    const option = event.currentTarget.closest('[data-role="option-column"]')
    if (!option) return
    option.remove()
    this.setupBlockDrag()
    this.refreshHiddenFields()
  }

  removeSection(event) {
    event.preventDefault()
    const section = event.currentTarget.closest('[data-target~="proposal-template-mapper.section"]')
    if (!section) return
    section.remove()
    this.setupBlockDrag()
    this.refreshHiddenFields()
    this.syncLibraryPanelPosition()
  }

  addDescriptionRule(event) {
    event.preventDefault()
    if (!this.hasDescriptionRuleTemplateTarget) return
    const wrap = event.currentTarget.closest('[data-role="description-rule-wrap"]')
    if (!wrap) return
    const container = wrap.querySelector('[data-role="description-rules"]')
    if (!container) return

    const node = this.descriptionRuleTemplateTarget.content.firstElementChild.cloneNode(true)
    container.appendChild(node)
    this.refreshHiddenFields()
  }

  removeDescriptionRule(event) {
    event.preventDefault()
    const row = event.currentTarget.closest('[data-role="description-rule-row"]')
    if (!row) return
    row.remove()
    this.refreshHiddenFields()
  }

  setupBlockDrag() {
    if (this.blockDrake) this.blockDrake.destroy()
    if (!this.hasBlockLibraryTarget) return

    const libraryContainers = Array.from(new Set(
      Array.from(this.blockLibraryTarget.querySelectorAll(".proposal-library-card"))
        .map((card) => card.parentElement)
        .filter((parent) => parent)
    ))
    if (libraryContainers.length === 0) return

    const containers = libraryContainers.concat(this.blockDropzoneTargets)
    if (containers.length <= libraryContainers.length) return

    this.blockDrake = dragula(containers, {
      copy: (_el, source) => libraryContainers.includes(source),
      moves: (el, source) => {
        if (!source) return false
        if (libraryContainers.includes(source)) return el.classList.contains("proposal-library-card")
        return el.classList.contains("template-block-card")
      },
      accepts: (_el, target) => target && target.classList.contains("template-block-dropzone")
    })
      .on("drop", (el, target, source) => {
        if (!target || libraryContainers.includes(target)) {
          if (libraryContainers.includes(target)) el.remove()
          this.refreshHiddenFields()
          return
        }
        if (libraryContainers.includes(source)) this.prepareAllocatedBlockCard(el)
        this.refreshHiddenFields()
      })
      .on("cancel", () => this.refreshHiddenFields())
      .on("remove", () => this.refreshHiddenFields())
  }

  prepareAllocatedBlockCard(card) {
    const blockId = card.dataset.blockId || ""
    const blockTitle = card.dataset.blockTitle || ""

    const replacement = document.createElement("div")
    replacement.className = "template-block-card card mb-2"
    replacement.dataset.blockId = blockId
    replacement.innerHTML = `
      <div class="card-body p-2 d-flex align-items-center justify-content-between" style="gap: 8px;">
        <div class="small"><strong>${this.escapeHtml(blockTitle)}</strong></div>
        <button type="button" class="btn btn-outline-danger btn-xs" style="padding: 0.08rem 0.35rem; font-size: 0.72rem;" data-action="proposal-template-mapper#removeAllocatedBlock">Remove</button>
        <input type="hidden" data-role="allocated-block-id" value="${this.escapeHtml(blockId)}" />
      </div>
    `
    card.replaceWith(replacement)
  }

  removeAllocatedBlock(event) {
    event.preventDefault()
    const card = event.currentTarget.closest(".template-block-card")
    if (!card) return
    card.remove()
    this.refreshHiddenFields()
  }

  refreshHiddenFields() {
    let sectionIndex = 0
    let bucketUid = 1

    this.sectionTargets.forEach((section) => {
      this.ensureSectionKey(section)
      const sectionNameInput = section.querySelector('[data-role="section-name"]')
      const sectionName = (sectionNameInput?.value || `Section ${sectionIndex + 1}`).trim()
      if (sectionNameInput) sectionNameInput.name = `template_sections[${sectionIndex}][name]`
      const coreOptionNameInput = section.querySelector('[data-role="core-option-name"]')
      const coreOptionName = (coreOptionNameInput?.value || "Option 1").trim()
      if (coreOptionNameInput) coreOptionNameInput.name = `template_sections[${sectionIndex}][core_name]`

      const suppressHidden = section.querySelector('[data-role="section-suppress-title-hidden"]')
      const suppressCheckbox = section.querySelector('[data-role="section-suppress-title"]')
      const includeHeaderHidden = section.querySelector('[data-role="section-include-header-hidden"]')
      const includeHeaderCheckbox = section.querySelector('[data-role="section-include-header"]')
      const includeOptionsHidden = section.querySelector('[data-role="section-include-options-hidden"]')
      const includeOptionsCheckbox = section.querySelector('[data-role="section-include-options"]')
      const includeFooterHidden = section.querySelector('[data-role="section-include-footer-hidden"]')
      const includeFooterCheckbox = section.querySelector('[data-role="section-include-footer"]')
      if (suppressHidden) suppressHidden.name = `template_sections[${sectionIndex}][suppress_title]`
      if (suppressCheckbox) suppressCheckbox.name = `template_sections[${sectionIndex}][suppress_title]`
      if (includeHeaderHidden) {
        includeHeaderHidden.name = `template_sections[${sectionIndex}][include_header]`
        includeHeaderHidden.value = "0"
      }
      if (includeHeaderCheckbox) includeHeaderCheckbox.name = `template_sections[${sectionIndex}][include_header]`
      if (includeOptionsHidden) {
        includeOptionsHidden.name = `template_sections[${sectionIndex}][include_options]`
        includeOptionsHidden.value = "0"
      }
      if (includeOptionsCheckbox) includeOptionsCheckbox.name = `template_sections[${sectionIndex}][include_options]`
      if (includeFooterHidden) {
        includeFooterHidden.name = `template_sections[${sectionIndex}][include_footer]`
        includeFooterHidden.value = "0"
      }
      if (includeFooterCheckbox) includeFooterCheckbox.name = `template_sections[${sectionIndex}][include_footer]`

      this.applySectionVisibility(section)

      const coreRulesContainer = section.querySelector('[data-role="description-rules"][data-rule-scope="core"]')
      if (coreRulesContainer) {
        coreRulesContainer.querySelectorAll('[data-role="description-rule-row"]').forEach((ruleRow, ruleIndex) => {
          const matcherInput = ruleRow.querySelector('[data-role="rule-matcher"]')
          const patternInput = ruleRow.querySelector('[data-role="rule-pattern"]')
          if (matcherInput) matcherInput.name = `template_sections[${sectionIndex}][core_description_rules][${ruleIndex}][matcher]`
          if (patternInput) patternInput.name = `template_sections[${sectionIndex}][core_description_rules][${ruleIndex}][pattern]`
        })
      }

      section.querySelectorAll('[data-role="option-column"]').forEach((option, optionIndex) => {
        const optionNameInput = option.querySelector('[data-role="option-name"]')
        if (optionNameInput) optionNameInput.name = `template_sections[${sectionIndex}][options][${optionIndex}][name]`
        const rulesContainer = option.querySelector('[data-role="description-rules"][data-rule-scope="option"]')
        if (rulesContainer) {
          rulesContainer.querySelectorAll('[data-role="description-rule-row"]').forEach((ruleRow, ruleIndex) => {
            const matcherInput = ruleRow.querySelector('[data-role="rule-matcher"]')
            const patternInput = ruleRow.querySelector('[data-role="rule-pattern"]')
            if (matcherInput) matcherInput.name = `template_sections[${sectionIndex}][options][${optionIndex}][description_rules][${ruleIndex}][matcher]`
            if (patternInput) patternInput.name = `template_sections[${sectionIndex}][options][${optionIndex}][description_rules][${ruleIndex}][pattern]`
          })
        }
      })

      section.querySelectorAll(".template-text-block-area").forEach((area) => {
        const kind = (area.dataset.bucketKind || "core").toLowerCase()
        const groupKind = kind === "option" ? "option" : (kind === "section" ? "section" : "core")
        const placement = (area.dataset.placement || "header").toLowerCase() === "footer" ? "footer" : "header"
        const optionName = groupKind === "option" ? this.optionNameForArea(area) : coreOptionName

        const base = `template_allocated_text_blocks[${bucketUid}]`
        const sectionInput = area.querySelector('[data-role="allocated-section-name"]')
        const kindInput = area.querySelector('[data-role="allocated-group-kind"]')
        const groupNameInput = area.querySelector('[data-role="allocated-group-name"]')
        const placementInput = area.querySelector('[data-role="allocated-placement"]')

        if (sectionInput) {
          sectionInput.name = `${base}[section_name]`
          sectionInput.value = sectionName
        }
        if (kindInput) {
          kindInput.name = `${base}[group_kind]`
          kindInput.value = groupKind
        }
        if (groupNameInput) {
          groupNameInput.name = `${base}[group_name]`
          groupNameInput.value = groupKind === "section" ? "" : optionName
        }
        if (placementInput) {
          placementInput.name = `${base}[placement]`
          placementInput.value = placement
        }

        area.querySelectorAll(".template-block-card").forEach((card) => {
          const idInput = card.querySelector('[data-role="allocated-block-id"]')
          if (idInput) idInput.name = `${base}[text_block_ids][]`
        })

        bucketUid += 1
      })

      sectionIndex += 1
    })
    this.syncLibraryPanelPosition()
  }

  syncLibraryPanelPosition() {
    const column = this.element.querySelector('[data-role="library-column"]')
    const panel = this.element.querySelector('[data-role="library-panel"]')
    if (!column || !panel) return

    if (window.innerWidth < 992) {
      panel.style.position = ""
      panel.style.top = ""
      panel.style.left = ""
      panel.style.width = ""
      panel.style.maxHeight = ""
      panel.style.overflowY = ""
      return
    }

    const rect = column.getBoundingClientRect()
    const reservedTop = this.computeReservedTopOffset()
    const desiredTop = Math.max(rect.top, reservedTop + 120)
    const maxHeight = Math.max(220, window.innerHeight - desiredTop - 10)

    panel.style.position = "fixed"
    panel.style.top = `${desiredTop}px`
    panel.style.left = `${Math.max(rect.left, 0)}px`
    panel.style.width = `${rect.width}px`
    panel.style.maxHeight = `${maxHeight}px`
    panel.style.overflowY = "auto"
  }

  computeReservedTopOffset() {
    const selectors = [
      ".navbar-fixed-top",
      ".navbar.fixed-top",
      "header .navbar",
      "#fixed-sidebar"
    ]

    let offset = 0
    selectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((el) => {
        const style = window.getComputedStyle(el)
        const isFixedOrSticky = style.position === "fixed" || style.position === "sticky"
        if (!isFixedOrSticky) return
        const rect = el.getBoundingClientRect()
        offset = Math.max(offset, rect.bottom)
      })
    })
    return offset
  }

  applySectionVisibility(section) {
    const includeHeader = section.querySelector('[data-role="section-include-header"]')?.checked !== false
    const includeOptions = section.querySelector('[data-role="section-include-options"]')?.checked !== false
    const includeFooter = section.querySelector('[data-role="section-include-footer"]')?.checked !== false

    const headerWrap = section.querySelector('[data-role="section-header-wrap"]')
    const optionsWrap = section.querySelector('[data-role="section-options-wrap"]')
    const footerWrap = section.querySelector('[data-role="section-footer-wrap"]')
    const addOptionBtn = section.querySelector('[data-action="proposal-template-mapper#addOption"]')

    if (headerWrap) headerWrap.classList.toggle("d-none", !includeHeader)
    if (optionsWrap) optionsWrap.classList.toggle("d-none", !includeOptions)
    if (footerWrap) footerWrap.classList.toggle("d-none", !includeFooter)
    if (addOptionBtn) addOptionBtn.classList.toggle("d-none", !includeOptions)
  }

  ensureSectionKey(section) {
    if (!section) return ""
    const existing = (section.dataset.sectionKey || "").trim()
    if (existing.length > 0) return existing
    const key = `section-${this.sectionKeyCounter}`
    this.sectionKeyCounter += 1
    section.dataset.sectionKey = key
    return key
  }

  nextOptionNameForSection(section) {
    const nums = []
    section.querySelectorAll('[data-role="option-name"]').forEach((input) => {
      const value = (input.value || "").trim()
      const match = value.match(/^Option\s+(\d+)$/i)
      if (match) nums.push(parseInt(match[1], 10))
    })

    const maxNum = nums.length > 0 ? Math.max(...nums) : 1
    return `Option ${maxNum + 1}`
  }

  optionNameForArea(area) {
    const optionNameInput = area.closest('[data-role="option-column"]')?.querySelector('[data-role="option-name"]')
    return (optionNameInput?.value || "").trim()
  }

  escapeHtml(value) {
    return (value || "").toString()
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\"/g, "&quot;")
      .replace(/'/g, "&#39;")
  }
}
